import { useTranslation } from "@localization/useTranslation.ts";
import { useTaskPaneContentDataMapping } from "./TaskPaneContent.hooks";
import { useTaskPaneContentStyles } from "./TaskPaneContent.styles";
import SubTasks from "../subTasks/SubTasks";
import { DotLegalButton, DotLegalFullScreenSpinner, DotLegalHeader, DotLegalPrettyScrollbar } from "@dotlegal/dotlegal-ui-components";
import TaskDocumentation from "./taskDocumentation/TaskDocumentation";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { Box, Button, Collapse, darken, IconButton, styled } from "@mui/material";
import Grid from "@mui/material/Grid2";
import MasterDataSection from "./masterDataSection/MasterDataSection";
import SubtasksSvg from "./SubTask.svg?react";
import Documentation from "./Documentation.svg?react";
import EvaluateIcon from "./evaluate.svg?react";
import TaskComments from "./taskComments/TaskComments";
import { AddTaskClickEnum } from "./TaskPaneContent.types";
import DeleteTaskDialog from "../deleteTaskDialog/DeleteTaskDialog";
import SendTaskReminderDialog from "./sendTaskReminderDialog/SendTaskReminderDialog";
import TaskLocked from "./task-locked.svg?react";
import TrialUsageWarning from "../../common/components/TrialUsageWarning.tsx";
import React, { RefObject, useRef, useState } from "react";
import { ContentSizeProvider, contentResponsiveSize, useContentSize } from "../../main/content/ContentSizeContext.tsx";
import PaneLayout from "../../main/panes/paneLayout/PaneLayout";
import { usePane } from "../../main/panes/paneLayout/usePane";
import { usePanes } from "../../main/panes/usePanes";
import ActivityPane from "../../annualWheel/activityPane/ActivityPane";
import { OpenableActivity, TaskPaneHeader, TaskPaneHeaderActions, TaskPaneSiblingNavigation, useActivityToOpen } from "./taskPaneHeader/TaskPaneHeader";
import { useTitleAlignedWithMainColumn } from "./taskPaneHeader/TaskPaneHeader.hooks";
import TaskRelationsBox from "./taskRelationsBox/TaskRelationsBox.tsx";
import MentionInput from "../../common/components/mentionInput/MentionInput.tsx";
import { EntityType } from "@common/Common.types.ts";
import MentionInputRender from "../../common/components/mentionInput/MentionInputRender.tsx";
import EvaluationQuestionnaireContent from "../../common/components/questionnaireComponents/evaluateQuestionnaireDialog/evaluationQuestionnaireContent/EvaluationQuestionnaireContent.tsx";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import CloseIcon from "@mui/icons-material/Close";
import DotLegalSection from "../../common/components/dotLegalSection/DotLegalSection.tsx";
import EvaluatedTaskSection from "./evaluatedTaskSection/EvaluatedTaskSection";

export interface ITaskContent {
    taskId: string;
    /** The task the host asked for. The activity arrows step away from it, and the replay arrow back to it. */
    openedTaskId: string;
    onNavigateToTask: (taskId: string) => void;
    setYearByTaskYear?: (year: number) => void;
}

export const buttonColor = "#F5F5F5";
export const TopButton = styled(Button)(({ theme }) => ({
    height: 40,
    boxShadow: "none",
    textTransform: "none",
    fontSize: 14,
    lineHeight: 1.5,
    borderRadius: "5px",
    backgroundColor: buttonColor,
    borderColor: buttonColor,
    color: theme.palette.primary.dark,
    "&:hover": {
        backgroundColor: darken(buttonColor, 0.1),
        boxShadow: "none",
    },
    "&:active": {
        boxShadow: "none",
        backgroundColor: darken(buttonColor, 0.1),
    },
    "& img": {
        marginRight: 8,
    },
    "&.Mui-disabled": {
        color: theme.palette.primary.dark,
    },
    padding: "8px",
}));

/**
 * The task lays itself out by the width of its own scrolling area, not the viewport: in a pane folded into
 * the rail it is a few hundred pixels wide while the window is thousands, and MUI's breakpoints only see
 * the window. The provider has to sit above the component that reads it, hence the split.
 */
export function TaskContent(props: ITaskContent) {
    const contentRef = useRef<HTMLDivElement>(null);

    return (
        <ContentSizeProvider contentRef={contentRef}>
            <TaskContentBody {...props} contentRef={contentRef} />
        </ContentSizeProvider>
    );
}

interface ITaskContentBodyProps extends ITaskContent {
    contentRef: RefObject<HTMLDivElement | null>;
}

function TaskContentBody(props: ITaskContentBodyProps) {
    const { translateString } = useTranslation();
    const { breakpoint } = useContentSize();
    const taskId = props.taskId;
    const {
        task,
        activityNavigation,
        backToOpenedTask,
        onDownloadClick,
        updateTaskState,
        onTaskDocumentChange,
        addTask,
        setAddTask,
        addDocument,
        setAddDocument,
        onAddDocumentationClick,
        documentationRef,
        evaluationSectionRef,
        showDeleteDialog,
        setShowDeleteDialog,
        showSendReminderDialog,
        setShowSendReminderDialog,
        taskUpdater,
        refetchTask,
        showEvaluation,
        onShowEvaluationClick,
        showEvaluateButton,
        evaluationUpdater,
        evaluationRef,
        onHideEvaluationClick,
        createEvaluationTask,
        isCreatingEvaluationTask,
    } = useTaskPaneContentDataMapping(props);

    const activityToOpen = useActivityToOpen(task);
    const { close } = usePane();
    const { open } = usePanes();
    const openObligation = (activity: OpenableActivity) => open(ActivityPane, activity);
    const styles = useTaskPaneContentStyles();
    const { permissions } = useUserContext();

    const showTaskDocuments = !!task;
    const showTaskComments = !!task;
    const showSubTasks = !!task;

    const [taskCommentsIsDoneLoading, setTaskCommentsIsDoneLoading] = useState<boolean>();
    const [subTasksIsDoneLoading, setSubTasksIsDoneLoading] = useState<boolean>();
    const [taskDocumentsIsDoneLoading, setTaskDocumentsIsDoneLoading] = useState<boolean>();
    const [masterDataSectionIsDoneLoading, setMasterDataSectionIsDoneLoading] = useState<boolean>();

    const notPartOfCurrentPlan = task?.notPartOfCurrentPlan;
    const showDialogData: boolean =
        ((showTaskComments ? taskCommentsIsDoneLoading : true) &&
            (showSubTasks ? subTasksIsDoneLoading : true) &&
            (showTaskDocuments ? taskDocumentsIsDoneLoading : true) &&
            masterDataSectionIsDoneLoading &&
            task !== undefined) ||
        (notPartOfCurrentPlan !== undefined && notPartOfCurrentPlan);

    const getValue = (value: string) => {
        return (
            <Box sx={styles.text}>
                <MentionInputRender value={value} />
            </Box>
        );
    };

    function resetStates() {
        setSubTasksIsDoneLoading(undefined);
        setTaskCommentsIsDoneLoading(undefined);
        setTaskDocumentsIsDoneLoading(undefined);
    }

    function onClose() {
        close();
        resetStates();
    }

    function showCreateEvaluationTask() {
        return Boolean(!task?.isProcessingActivityValidation && task?.evaluationStatus && permissions.taskPermissions.create);
    }

    const buttonIconWrapper = { marginRight: 1, "& svg": { height: 14 }, display: "flex" };

    const mainColumnSize = contentResponsiveSize(breakpoint, { xs: 12, sm: 12, md: 7 });
    // Narrow enough that the two columns stack. Reversed so the master data, which is what the reader came
    // for, stays at the top rather than sitting below the whole documentation and comment history.
    const isSingleColumn = mainColumnSize === 12;
    const { titleRef, mainColumnRef } = useTitleAlignedWithMainColumn(isSingleColumn, task !== undefined && !notPartOfCurrentPlan);

    const content = (
        <Grid container spacing={2} sx={isSingleColumn ? { flexDirection: "column-reverse" } : undefined}>
            <Grid size={mainColumnSize} ref={mainColumnRef}>
                <DotLegalPrettyScrollbar>
                    <>
                        {showDialogData && (
                            <Box sx={{ "& > *:not(:last-child) ": { marginRight: 1 }, marginBottom: 2 }}>
                                <TopButton
                                    disabled={!permissions.taskPermissions.edit}
                                    onClick={() => {
                                        setAddTask(true);
                                    }}
                                >
                                    <Box sx={buttonIconWrapper}>
                                        <SubtasksSvg />
                                    </Box>
                                    {translateString("addSubTask")}
                                </TopButton>

                                <TopButton disabled={!permissions.taskPermissions.edit} onClick={onAddDocumentationClick}>
                                    <Box sx={buttonIconWrapper}>
                                        <Documentation />
                                    </Box>
                                    {translateString("addDocumentation")}
                                </TopButton>
                                {showEvaluateButton && (
                                    <TopButton disabled={!permissions.taskPermissions.edit} onClick={() => onShowEvaluationClick()}>
                                        <Box sx={buttonIconWrapper}>
                                            <EvaluateIcon />
                                        </Box>
                                        {translateString("evaluateTask")}
                                    </TopButton>
                                )}
                            </Box>
                        )}
                        {showDialogData && (task?.description || task?.isAdHocTask) && (
                            <DotLegalSection>
                                <DotLegalHeader marginBottom={1} marginTop={0} headerStyle="small">
                                    {translateString("description")}
                                </DotLegalHeader>
                                {task.isAdHocTask ? (
                                    <MentionInput
                                        mentionSource={{ entityFromId: taskId, entityFromType: EntityType.Task }}
                                        disabled={!permissions.taskPermissions.edit}
                                        placeholder={translateString("addDescription")}
                                        variant="standard"
                                        noMargin
                                        value={task.description}
                                        debounce
                                        onChange={async (description) => taskUpdater.onDescriptionChange(description)}
                                        multiline
                                    />
                                ) : (
                                    getValue(task!.description)
                                )}
                            </DotLegalSection>
                        )}
                        {showSubTasks && (
                            <SubTasks
                                passIsDoneLoading={(newValue: boolean) => {
                                    if (!subTasksIsDoneLoading) {
                                        setSubTasksIsDoneLoading(newValue);
                                    }
                                }}
                                disabled={!permissions.taskPermissions.edit}
                                taskId={taskId}
                                updateTaskState={() => updateTaskState()}
                                shouldAddTask={addTask}
                                onSuccessAddTask={() => setAddTask(false)}
                                allDataIsDoneLoading={showDialogData}
                                isAdHocTask={task.isAdHocTask}
                            />
                        )}
                        {showTaskDocuments && (
                            <Box ref={documentationRef}>
                                <TaskDocumentation
                                    passIsDoneLoading={(newValue: boolean) => {
                                        if (!taskDocumentsIsDoneLoading) {
                                            setTaskDocumentsIsDoneLoading(newValue);
                                        }
                                    }}
                                    disabled={!permissions.taskPermissions.edit}
                                    taskId={taskId}
                                    taskName={task?.name}
                                    updateTaskState={() => updateTaskState()}
                                    updateDocumentState={(hasDocuments) => onTaskDocumentChange(hasDocuments)}
                                    shouldAddDocument={addDocument === AddTaskClickEnum.ShowFileExplorer}
                                    show={addDocument >= AddTaskClickEnum.ShowSection}
                                    isDocumentationRequired={task?.documentationRequired}
                                    onFileDialogClose={() => {
                                        if (addDocument === AddTaskClickEnum.ShowFileExplorer) {
                                            setAddDocument(AddTaskClickEnum.ShowSection);
                                        }
                                    }}
                                    onClose={() => setAddDocument(AddTaskClickEnum.None)}
                                    allDataIsDoneLoading={showDialogData}
                                />
                            </Box>
                        )}

                        {showEvaluation && showDialogData && (
                            <Box
                                ref={evaluationSectionRef}
                                sx={{
                                    position: "relative",
                                    "&:hover .evaluation-close-button": {
                                        visibility: "visible",
                                    },
                                }}
                            >
                                <DotLegalSection>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 0.5 }}>
                                        <DotLegalHeader marginTop={0} marginBottom={0} headerStyle="small">
                                            {translateString("evaluation")}
                                        </DotLegalHeader>
                                        <IconButton
                                            className="evaluation-close-button"
                                            aria-label="close"
                                            onClick={onHideEvaluationClick}
                                            sx={{
                                                position: "absolute",
                                                top: 8,
                                                right: 8,
                                                padding: 0.5,
                                                visibility: "hidden",
                                            }}
                                        >
                                            <CloseIcon sx={(theme) => ({ fill: theme.palette.error.main })} />
                                        </IconButton>
                                    </Box>
                                    <Box>
                                        <EvaluationQuestionnaireContent
                                            inputRef={evaluationRef}
                                            evaluationStatus={task?.evaluationStatus}
                                            evaluation={task?.evaluation}
                                            onEvaluationStateChange={evaluationUpdater.onEvaluationStatusChange}
                                            onEvaluationChange={evaluationUpdater.onEvaluationChange}
                                            debounce
                                            disableAutoFocus={!!task?.evaluationStatus || !isNullOrWhitespace(task?.evaluation)}
                                            useMentionInput
                                            entityId={taskId}
                                            entityType={EntityType.Task}
                                        />
                                        <Collapse in={showCreateEvaluationTask()}>
                                            <Box sx={{ marginTop: 2, display: "flex", justifyContent: "flex-end" }}>
                                                <DotLegalButton
                                                    onClick={() => createEvaluationTask()}
                                                    buttonType={"primary"}
                                                    isLoading={isCreatingEvaluationTask}
                                                >
                                                    {translateString("createEvaluationTask")}
                                                </DotLegalButton>
                                            </Box>
                                        </Collapse>
                                    </Box>
                                </DotLegalSection>
                            </Box>
                        )}

                        {task?.evaluatedTask && showDialogData && <EvaluatedTaskSection evaluationTask={task} evaluatedTask={task.evaluatedTask} />}
                    </>
                    {showTaskComments && (
                        <TaskComments
                            passIsDoneLoading={(newValue: boolean) => {
                                if (!taskCommentsIsDoneLoading) {
                                    setTaskCommentsIsDoneLoading(newValue);
                                }
                            }}
                            taskId={taskId}
                            taskName={task.name}
                            customId={task.customId}
                            deadline={task.deadline}
                            allDataIsDoneLoading={showDialogData}
                        />
                    )}
                </DotLegalPrettyScrollbar>
            </Grid>
            <Grid hidden={!showDialogData} size={contentResponsiveSize(breakpoint, { xs: 12, sm: 12, md: 5 })}>
                <MasterDataSection
                    onOpenActivity={activityToOpen ? () => openObligation(activityToOpen) : undefined}
                    passIsDoneLoading={(newValue: boolean) => {
                        if (!masterDataSectionIsDoneLoading) {
                            setMasterDataSectionIsDoneLoading(newValue);
                        }
                    }}
                    taskId={task?.id}
                    isSingleColumn={isSingleColumn}
                    masterData={
                        task
                            ? {
                                  customId: task.customId,
                                  deadline: task.deadline,
                                  deadlineMaxDate: task.deadlineMaxDate,
                                  documentationRequired: task.documentationRequired,
                                  hasDocument: task.hasDocument,
                                  isOverdue: task.isOverdue,
                                  isAdHocTask: task.isAdHocTask,
                                  legalEntityName: task.legalEntityName,
                                  name: task.name,
                                  isProcessingActivityValidation: task.isProcessingActivityValidation,
                                  annualWheelActivityGeneralType: task.annualWheelActivityGeneralType,
                                  frameworks: [],
                              }
                            : undefined
                    }
                    isLoading={!task}
                    refetchTask={() => refetchTask()}
                />
                <TaskRelationsBox canEdit={permissions.taskPermissions.edit} taskId={taskId} annualWheelActivityId={task?.annualWheelActivityId} />
            </Grid>
        </Grid>
    );
    const notPartOfPlanContent = (
        <Box sx={{ height: 500, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ mt: 3 }}>
                <TrialUsageWarning textKey={"taskNotPartOfPlanWarning"} headerKey={"taskIsLocked"} />
            </Box>
            <Box sx={{ mt: 8 }}>
                <TaskLocked />
            </Box>
        </Box>
    );

    return (
        <PaneLayout
            header={<TaskPaneHeader task={task} onNameChange={taskUpdater.onNameChange} onOpenObligation={openObligation} titleRef={titleRef} />}
            actions={
                <TaskPaneHeaderActions
                    task={task}
                    onDeleteClick={() => setShowDeleteDialog(true)}
                    onSendReminderClick={() => setShowSendReminderDialog(true)}
                    onDownloadClick={() => onDownloadClick()}
                    onOpenObligation={openObligation}
                />
            }
        >
            <Box ref={props.contentRef} sx={styles.contentArea}>
                {activityNavigation && (
                    <Box sx={{ marginTop: -1.375 }}>
                        <TaskPaneSiblingNavigation
                            activityNavigation={activityNavigation}
                            backToOpenedTask={backToOpenedTask}
                            onNavigateToTask={props.onNavigateToTask}
                        />
                    </Box>
                )}

                {!showDialogData && (
                    <Box sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <DotLegalFullScreenSpinner />
                    </Box>
                )}

                {notPartOfCurrentPlan ? notPartOfPlanContent : content}
            </Box>

            {showSendReminderDialog && <SendTaskReminderDialog taskId={taskId} onCloseDialog={() => setShowSendReminderDialog(false)} />}

            {showDeleteDialog && task && (
                <DeleteTaskDialog
                    taskId={taskId!}
                    taskName={task.name}
                    onCloseDialog={() => setShowDeleteDialog(false)}
                    onDeleteClick={() => {
                        setShowDeleteDialog(false);
                        onClose();
                    }}
                />
            )}
        </PaneLayout>
    );
}

export default TaskContent;
